import { Fragment, type ReactNode } from "react";
import { graphql, readInlineData } from "react-relay";

import { AdSlot } from "~/components/advertisement/ad-slots/ad-slot";
import { Comment } from "~/components/comment";
import { BaseLinkContextProvider } from "~/components/common/base-link/context";
import { MostPopular } from "~/components/most-popular";
import { Multimedia } from "~/components/multimedia";
import { AiArticleWidget } from "~/components/section/ai-article-widget";
import { useSectionContext } from "~/components/section/contexts";
import { TrendingTopic } from "~/components/trending-topic";
import { section as sectionData } from "~/data/section";
import type { hooksSectionWidgetQuery$key } from "~/queries/__generated__/hooksSectionWidgetQuery.graphql";

import { getDefaultSectionAdsConfigs } from "./ads-config";
import {
  CommentContainer,
  FifthAds,
  FirstAds,
  FourthAds,
  MostPopularContainer,
  MultimediaContainer,
  RHSModule1,
  RHSModule2,
  RHSModule3,
  RHSModule4,
  RHSModule5,
  RHSModule6,
  SecondAds,
  SeventhAds,
  SixthAds,
  ThirdAds,
  TrendingTopicContainer,
} from "./styles";

export const useRenderAdsAndSectionWidgets = (
  reference: hooksSectionWidgetQuery$key,
  LHSModuleLength: number,
) => {
  const { advertisement: baseAdvertisement } = useSectionContext();
  const data = readInlineData(
    graphql`
      fragment hooksSectionWidgetQuery on Query
      @inline
      @argumentDefinitions(
        sectionId: { type: "String!" }
        commentHarrySectionIds: { type: "[String]" }
        commentQueueName: { type: "String!" }
        mostPopularQueueName: { type: "String!" }
        multimediaQueueName: { type: "String!" }
        scmpPlusPaywallTypeIds: { type: "[String]", defaultValue: [] }
        trendingTopicsQueueName: { type: "String!" }
      ) {
        section: section(filter: { entityId: $sectionId }) {
          entityId
          advertZone(version: 2)
        }
        ...commentQueueQuery
          @arguments(
            commentHarrySectionIds: $commentHarrySectionIds
            commentLimit: 6
            commentQueueName: $commentQueueName
            scmpPlusPaywallTypeIds: $scmpPlusPaywallTypeIds
            withHarrysView: false
          )
        ...mostPopularQueueQuery @arguments(mostPopularQueueName: $mostPopularQueueName)
        ...multimediaQuery @arguments(multimediaQueueName: $multimediaQueueName)
        ...trendingTopicTopic @arguments(trendingTopicsQueueName: $trendingTopicsQueueName)
      }
    `,
    reference,
  );

  const renderComment = () => (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: "opinion",
        pgtype: "section",
      }}
    >
      <CommentContainer>
        <Comment reference={data} withMoreCommentButton={false} />
      </CommentContainer>
    </BaseLinkContextProvider>
  );
  const renderMultimedia = () => (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: "multimedia",
        pgtype: "section",
      }}
    >
      <MultimediaContainer>
        <Multimedia reference={data} />
      </MultimediaContainer>
    </BaseLinkContextProvider>
  );
  const renderMostPopular = () => (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: "most_popular",
        pgtype: "section",
      }}
    >
      <MostPopularContainer>
        <MostPopular reference={data} />
      </MostPopularContainer>
    </BaseLinkContextProvider>
  );
  const renderTrendingTopics = () => (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: "trending_topics",
        pgtype: "section",
      }}
    >
      <TrendingTopicContainer>
        <TrendingTopic reference={data} />
      </TrendingTopicContainer>
    </BaseLinkContextProvider>
  );
  const renderRecommendedForYou = () => (
    <AiArticleWidget sectionIds={baseAdvertisement.targeting.scsid ?? []} />
  );

  const renderLatestAsiaNews = () => <>LatestAsiaNews</>;
  const renderHighlightsFromStyle = () => <>HighlightsFromStyle</>;
  const renderHighlightsFromPostMagazine = () => <>HighlightsFromPostMagazine</>;
  const renderSCMPColumnists = () => <>SCMPColumnists</>;
  const renderHarrysView = () => <>HarrysView</>;
  const renderHighlightsFromHKRacing = () => <>HighlightsFromHKRacing</>;
  const renderONE = () => <>ONE</>;

  const rhsModuleComponents = [
    RHSModule1,
    RHSModule2,
    RHSModule3,
    RHSModule4,
    RHSModule5,
    RHSModule6,
  ];

  const defaultRHSModuleMap = [
    renderComment,
    renderRecommendedForYou,
    renderMultimedia,
    renderMostPopular,
  ];

  const rhsModuleMap: Record<string, Array<() => ReactNode>> = {
    [sectionData.china.entityId]: [
      renderComment,
      renderRecommendedForYou,
      renderMultimedia,
      renderMostPopular,
    ],
    [sectionData.economy.entityId]: [
      renderComment,
      renderRecommendedForYou,
      renderMultimedia,
      renderMostPopular,
    ],
    [sectionData.hongKong.entityId]: [
      renderComment,
      renderTrendingTopics,
      renderRecommendedForYou,
      renderMultimedia,
      renderMostPopular,
    ],
    [sectionData.thisWeekInAsia.entityId]: [
      renderLatestAsiaNews,
      renderComment,
      renderRecommendedForYou,
      renderMultimedia,
      renderMostPopular,
    ],
    [sectionData.business.entityId]: [
      renderComment,
      renderRecommendedForYou,
      renderMultimedia,
      renderMostPopular,
    ],
    [sectionData.tech.entityId]: [
      renderComment,
      renderTrendingTopics,
      renderRecommendedForYou,
      renderMultimedia,
    ],
    [sectionData.lifestyle.entityId]: [
      renderHighlightsFromStyle,
      renderComment,
      renderRecommendedForYou,
      renderMultimedia,
      renderMostPopular,
    ],
    [sectionData.culture.entityId]: [
      renderHighlightsFromPostMagazine,
      renderComment,
      renderRecommendedForYou,
      renderMultimedia,
    ],
    [sectionData.world.entityId]: [
      renderComment,
      renderTrendingTopics,
      renderRecommendedForYou,
      renderMultimedia,
      renderMostPopular,
    ],
    [sectionData.comment.entityId]: [
      renderSCMPColumnists,
      renderHarrysView,
      renderRecommendedForYou,
      renderMultimedia,
    ],
    [sectionData.sport.entityId]: [
      renderHighlightsFromHKRacing,
      renderONE,
      renderComment,
      renderRecommendedForYou,
      renderMultimedia,
      renderMostPopular,
    ],
  };

  const adSlotComponents = [SecondAds, ThirdAds, FourthAds, FifthAds, SixthAds, SeventhAds];
  const adConfigs = getDefaultSectionAdsConfigs(
    data.section.entityId,
    data?.section?.advertZone ?? undefined,
  );
  const [[firstAdConfigDesktop, firstAdConfigMobile], ...restSdConfigs] = adConfigs;
  const rhsModule = rhsModuleMap[sectionData.hongKong.entityId] ?? defaultRHSModuleMap;

  let updatedRHSModule = rhsModule;

  if (LHSModuleLength / 2 < rhsModule.length) {
    const removeCount = rhsModule.length - Math.floor(LHSModuleLength / 2);
    updatedRHSModule = rhsModule.slice(0, -removeCount);
  }

  const render = () => (
    <>
      <FirstAds>
        <AdSlot {...firstAdConfigDesktop} />
        <AdSlot {...firstAdConfigMobile} />
      </FirstAds>
      {/* temp using HK section layout for all */}
      {/* later should change back to 'data.section.entityId' */}
      {updatedRHSModule.map((rhsModule, index) => {
        const Wrapper = rhsModuleComponents[index];
        const AdSlotComponent = adSlotComponents[index];
        const [desktopAdsConfig, mobileAdsConfig] = restSdConfigs[index] ?? [];
        return (
          <Fragment key={index}>
            <Wrapper>{rhsModule()}</Wrapper>
            {(desktopAdsConfig || mobileAdsConfig) && (
              <AdSlotComponent>
                {desktopAdsConfig && <AdSlot {...desktopAdsConfig} />}
                {mobileAdsConfig && <AdSlot {...mobileAdsConfig} />}
              </AdSlotComponent>
            )}
          </Fragment>
        );
      })}
    </>
  );

  return { render };
};
